import cx from 'classnames';
import React, { useEffect, useState, useRef } from 'react';
import YouTube from 'react-youtube';
import { useDispatch, useSelector } from 'react-redux';
import { useLocation } from 'react-router-dom';
import {
  AUTH_TOKEN,
  CURRENT_USER,
  EMAIL_VERIFIED,
  REFRESH_TOKEN,
} from '../../constants/projectConstants';
import { apiGet } from '../../helpers/api';
import { getLocalStorage, youtubeParser } from '../../helpers/jsHelper';
import {
  learnByVideoActions,
  selectIsAutoplay,
  selectIsVideoModalCentered,
  selectShow,
  selectVideo,
  selectMattressDataVideo
} from '../../redux/slicers/learnByVideoSlicer';
import styles from './LearnVideoModel.module.scss';
import { TUTORIAL_API } from '../../config/constant/apiConstant';
import { dispatcher } from '../../helpers/projectHelper';
import { getAppState } from '../../redux/store';
import { warningToast } from '../../helpers/toastHelper';
import Draggable from 'react-draggable';
import { Button } from 'react-bootstrap';
import { icons } from '../../assets';
import { Portal } from '../../common/Portal';
import { debounce } from 'lodash';
import { isMattressBuilderPage } from '../admin/curator/ProductBuilderComponents/commonHelper';
import { selectCuratorInitFinished } from '../../redux/slicers/admin/curatorSlicer';
import { isCuratorPage } from '../../helpers/urlHelper';
const { CloseIconSmall, ExpandIcon, DragIcon } = icons;

export const openTutorialVideoModal = () => {
  const haveId = selectVideo(getAppState());
  if (!haveId) {
    warningToast('Tutorial video is not available right now.');
    return;
  }
  dispatcher(learnByVideoActions.show());
};

const initialPosition = { x: 0, y: 0 };

// The two sizes the modal toggles between. The stylesheet reads the active one off the
// --modal-content-width custom property, so these stay the single source of truth.
const CONTENT_WIDTH = { expanded: 1170, small: 500 };
// 1rem of padding on each side of a 16:9 player.
const MODAL_PADDING = 16;
const VIDEO_RATIO = 9 / 16;

// A fixed element is laid out against the viewport minus its scrollbars, which is what
// these report - window.innerWidth would include the scrollbar and push the modal past
// the edge.
const getViewport = () => ({
  width: document.documentElement.clientWidth,
  height: document.documentElement.clientHeight,
});

// The modal's size follows entirely from its width, so it can be worked out up front
// rather than measured. Measuring would read a half-finished value mid-transition.
const getModalSize = (isExpanded) => {
  const width = Math.min(
    (isExpanded ? CONTENT_WIDTH.expanded : CONTENT_WIDTH.small) + MODAL_PADDING * 2,
    getViewport().width
  );
  return { width, height: (width - MODAL_PADDING * 2) * VIDEO_RATIO + MODAL_PADDING * 2 };
};

// Expanded rests in the middle of the screen, collapsed in the bottom right corner.
// Both are expressed as a drag offset from the top left so the two states differ by
// nothing but a transform, which is what lets the toggle animate.
const getRestingPosition = (isExpanded) => {
  const { width, height } = getModalSize(isExpanded);
  const viewport = getViewport();
  return isExpanded
    ? { x: (viewport.width - width) / 2, y: (viewport.height - height) / 2 }
    : { x: viewport.width - width, y: viewport.height - height };
};

export default function LearnVideoModel() {
  const dispatch = useDispatch();
  const videoId = useSelector(selectVideo);
  const selectMattressDataVideoId = useSelector(selectMattressDataVideo);
  const showVideo = useSelector(selectShow);
  const isCentered = useSelector(selectIsVideoModalCentered);
  const isAutoplay = useSelector(selectIsAutoplay);
  const location = useLocation();
  const [player, setPlayer] = useState(null);
  const [playerState, setPlayerState] = useState(null);
  const [position, setPosition] = useState(initialPosition);
  const [isDragging, setIsDragging] = useState(false);
  const [wasOpen, setWasOpen] = useState(false);
  const [canAnimate, setCanAnimate] = useState(false);
  const positionRef = useRef(position);
  positionRef.current = position;
  const videoContainerRef = useRef(null);
  const curatorInitialized = useSelector(selectCuratorInitFinished);

  useEffect(() => {
    const listener = debounce(() => {
      if (!videoContainerRef?.current) return;
      const clientRect = videoContainerRef?.current?.getBoundingClientRect();
      const { top, left, right } = clientRect;
      const { x, y } = positionRef.current;
      let newX = x;
      let newY = y;
      if (left < 0) {
        newX = x + Math.abs(left);
      } else {
        const rightDiff = right - window.innerWidth;
        if (rightDiff > 0) {
          if (rightDiff > left) {
            newX = x - left;
          } else {
            newX = x - rightDiff;
          }
        }
      }
      if (top < 0) {
        newY = y + Math.abs(top);
      }
      if (x !== newX || y !== newY) {
        setPosition({ x: newX, y: newY });
      }
      if (window.innerHeight < 400) {
        setPosition(getRestingPosition(false));
        dispatch(learnByVideoActions.setCentered(false));
      }
    }, 60);
    window.addEventListener('resize', listener);
    return () => {
      window.removeEventListener('resize', listener);
      listener.cancel();
    };
  }, []);

  // Opening has to place the modal during the render itself. An effect - even a layout
  // one - runs too late here, because Portal only resolves its container afterwards, so
  // the modal would mount at wherever it was last left and slide across from there.
  if (showVideo !== wasOpen) {
    setWasOpen(showVideo);
    if (showVideo) setPosition(getRestingPosition(isCentered));
  }

  // Resizing between the two sizes while open should glide, so that one is an effect.
  useEffect(() => {
    if (!showVideo) return;
    setPosition(getRestingPosition(isCentered));
  }, [isCentered]);

  // Easing is switched on a frame after opening: the modal should simply be in its
  // corner when it appears, and only animate once the user starts toggling it.
  useEffect(() => {
    if (!showVideo) {
      setCanAnimate(false);
      return;
    }
    const frame = requestAnimationFrame(() => setCanAnimate(true));
    return () => cancelAnimationFrame(frame);
  }, [showVideo]);

  useEffect(() => {
    addRemoveListener(true);
    return () => {
      addRemoveListener(false);
    };
  }, [player, playerState]);

  useEffect(() => {
    // if (isMattressBuilderPage()) return; // Skip API call if on Mattress Builder page
    if (curatorInitialized || !isCuratorPage()) apiGetTutorialVideos(); // Fetch tutorial API if curator is initialized or not on curator page
  }, [location, curatorInitialized]);

  const apiGetTutorialVideos = () => {
    if (!checkUserHasAccess() || videoId) {
      return;
    }
    apiGet(TUTORIAL_API).then((response) => {
      if (response && response?.results?.length) {
        const url = youtubeParser(response?.results?.[0]?.url);
        dispatch(learnByVideoActions.setData(url));
      }
    });
  };

  const checkUserHasAccess = () => {
    if (
      getLocalStorage(AUTH_TOKEN) &&
      getLocalStorage(REFRESH_TOKEN) &&
      getLocalStorage(EMAIL_VERIFIED) &&
      getLocalStorage(CURRENT_USER)
    ) {
      return true;
    }
    return false;
  };

  const addRemoveListener = (flag) => {
    if (flag) {
      document.addEventListener('visibilitychange', handlePlayNPause);
    } else {
      document.removeEventListener('visibilitychange', handlePlayNPause);
    }
  };

  const handlePlayNPause = () => {
    if (document.hidden) {
      if (playerState === 2) {
        addRemoveListener(false);
        return;
      }
      player?.pauseVideo();
    } else {
      player?.playVideo();
    }
  };

  const onReady = (event) => {
    setPlayer(event.target);
  };

  const onStateChange = (event) => {
    setPlayerState(event.data);
  };

  const handleClose = () => {
    dispatch(learnByVideoActions.close());
  };

  const toggleExpandAndCenter = () => {
    dispatch(learnByVideoActions.setCentered(!isCentered));
  };

  const onDrag = (e, position) => {
    setPosition({ x: position.x, y: position.y });
  };

  if (!showVideo) return null;

  return (
    <Portal>
      <Draggable
        bounds="parent"
        handle=".video-drag"
        onStart={() => setIsDragging(true)}
        onDrag={onDrag}
        onStop={() => setIsDragging(false)}
        position={position}
        nodeRef={videoContainerRef}>
        <div
          className={cx(styles.videoModal, {
            [styles.small]: !isCentered,
            [styles.animatable]: canAnimate,
            [styles.dragging]: isDragging,
          })}
          ref={videoContainerRef}
          style={{
            '--modal-content-width': `${
              isCentered ? CONTENT_WIDTH.expanded : CONTENT_WIDTH.small
            }px`,
          }}>
          <Button onClick={toggleExpandAndCenter} className={cx('modalCloseButton', styles.scale)}>
            <ExpandIcon />
          </Button>
          <Button className={cx('modalCloseButton', 'video-drag', styles.drag)}>
            <DragIcon />
          </Button>
          <Button onClick={handleClose} className={cx('modalCloseButton', styles.close)}>
            <CloseIconSmall />
          </Button>
          <Video
            srcId={isMattressBuilderPage() ? selectMattressDataVideoId : videoId}
            autoplay={isAutoplay}
            onReady={onReady}
            onStateChange={onStateChange}
          />
        </div>
      </Draggable>
    </Portal>
  );
}

const Video = ({ srcId, autoplay = 0, onReady, onStateChange }) => {
  if (!srcId) return null;
  const opts = {
    playerVars: {
      autoplay: autoplay,
    },
  };

  return (
    <div className={styles.videoResponsive}>
      <YouTube videoId={srcId} opts={opts} onReady={onReady} onStateChange={onStateChange} />
    </div>
  );
};
